import { PreQualificationStep } from 'enums/PreQualificationStep';
import { PrequalificationNextStep } from 'prequalificationV2/api/PreQualification/preQualificationApi.enums';
import { ApplicationType } from 'enums/ApplicationType';

export enum NextStepDestination {
  Prequalification = 'Prequalification',
  CreateAccount = 'CreateAccount',
  Dashboard = 'Dashboard',
}

export interface NextStepNavigation {
  destination: NextStepDestination;
  prequalificationStep?: PreQualificationStep;
}

const MAP_NEXT_STEP_TO_PREQUALIFICATION_STEP: Partial<Record<PrequalificationNextStep, PreQualificationStep>> = {
  [PrequalificationNextStep.PosOffers]: PreQualificationStep.AcceptPosAutomatedOffer,
  [PrequalificationNextStep.HilOffers]: PreQualificationStep.AcceptHilAutomatedOffer,
  [PrequalificationNextStep.PropertyInformation]: PreQualificationStep.PropertyInformation,
  [PrequalificationNextStep.LoanInformation]: PreQualificationStep.LoanInformation,
  [PrequalificationNextStep.AdditionalCoBorrowerInfo]: PreQualificationStep.AdditionalCoBorrowerInfo,
  [PrequalificationNextStep.Decline]: PreQualificationStep.NoOptions,
};

/**
 * Single source of truth translating a backend `PrequalificationNextStep` into a navigation intent,
 * shared by the login/OTP recovery path (`checkBorrower`) and the in-flow underwriting path
 * (`useNavigateAfterUnderwriting`). `CreateAccount` maps to a `SignInStep` on the Login route, every
 * offer/information step maps to a prequal sub-step, and the Dashboard covers the rest.
 */
export const resolveNextStepNavigation = (
  step: PrequalificationNextStep | null,
  loanType?: ApplicationType,
): NextStepNavigation => {
  if (step === PrequalificationNextStep.CreateAccount) {
    return { destination: NextStepDestination.CreateAccount };
  }

  if (step === PrequalificationNextStep.NoApplicationInProgress) {
    return { destination: NextStepDestination.Dashboard };
  }

  if (step === PrequalificationNextStep.FirstAfterSignInStep && loanType === ApplicationType.Joint) {
    return {
      destination: NextStepDestination.Prequalification,
      prequalificationStep: PreQualificationStep.CoBorrowerInfo,
    };
  }

  if (step === PrequalificationNextStep.FirstAfterSignInStep) {
    return {
      destination: NextStepDestination.Prequalification,
      prequalificationStep: PreQualificationStep.LoanInformation,
    };
  }

  const prequalificationStep = step && MAP_NEXT_STEP_TO_PREQUALIFICATION_STEP[step];

  return prequalificationStep
    ? { destination: NextStepDestination.Prequalification, prequalificationStep: prequalificationStep }
    : { destination: NextStepDestination.Dashboard };
};
